import { type FC, useContext } from "react";

import { BTText } from "@btnet/core-components";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { dispatchTracking } from "utilities/analytics/analytics";
import { routes } from "utilities/routes";
import { isOrgOwner } from "utilities/user/user";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";

import "./PayOnlineMarketingModal.less";

export interface IPayOnlineMarketingModalProps {
    isVisible: boolean;
    onClose: () => void;
    sourcePage: string;
}

export const PayOnlineMarketingModal: FC<IPayOnlineMarketingModalProps> = ({
    isVisible,
    onClose,
    sourcePage,
}) => {
    const userInfo = useContext(UserInfoContext);
    const webviewContext = useWebviewContext();
    const canSignUp = isOrgOwner(userInfo ?? undefined);

    const handleLearnMore = () => {
        dispatchTracking({
            event: "ButtonClick",
            page: sourcePage,
            modal: "PayOnlineMarketingModal",
            element: "LearnMore",
        });
        if (webviewContext) {
            window.open(
                `${routes.appBase}/webview/${webviewContext.platform}/${webviewContext.mobileVersion}/PaymentsOverview`,
                "_self",
            );
        } else {
            window.open(
                routes.appBase + routes.onlinePayments.paymentOverview,
                "_blank",
                "noopener,noreferrer",
            );
        }
        onClose();
    };

    const handleSignUp = () => {
        dispatchTracking({
            event: "ButtonClick",
            page: sourcePage,
            modal: "PayOnlineMarketingModal",
            element: "SignUp",
        });
        if (webviewContext) {
            window.open(
                `${routes.appBase}/webview/${webviewContext.platform}/${webviewContext.mobileVersion}/PaymentsHostedOnboarding`,
                "_self",
            );
        } else {
            window.open(
                routes.appBase + routes.onlinePayments.paymentsHostedOnboarding,
                "_blank",
                "noopener,noreferrer",
            );
        }
        onClose();
    };

    return (
        <BTModal
            data-testid="payOnlineMarketingModal"
            className="PayOnlineMarketingModalWrapper"
            visible={isVisible}
            width="480px"
            beforeClose={onClose}
            useModalLayout
        >
            <BTModalLayout
                title="Pay subs/vendors online"
                modalConfig={{ parentRoute: "", beforeClose: onClose }}
            >
                <div className="flex flex-col gap-4" data-testid="payOnlineMarketingModalBody">
                    <BTText
                        size="normal-md"
                        text="Pay bills directly in Buildertrend and keep your project finances connected in one place. Apply for online payments to get started."
                    />
                    <div className="flex flex-row justify-end gap-2 mt-6">
                        <BTButton
                            data-testid="payOnlineMarketingLearnMore"
                            type="secondary"
                            onClick={handleLearnMore}
                        >
                            Learn more
                        </BTButton>
                        {canSignUp && (
                            <BTButton
                                data-testid="payOnlineMarketingSignUp"
                                type="primary"
                                onClick={handleSignUp}
                            >
                                Sign up
                            </BTButton>
                        )}
                    </div>
                </div>
            </BTModalLayout>
        </BTModal>
    );
};
